/**
 * vmx-pdp-gallery.js
 * <vmx-pdp-gallery> Web Component.
 *
 * Responsibilities:
 *  - Thumbnail click → swap main image (src / alt).
 *  - Listen for vmx:variant:changed → swap main image to variant's image.
 *  - Prev/Next navigation with active-state management.
 *  - Frame counter update.
 *
 * No innerHTML mutations — uses setAttribute / textContent only.
 */

class VmxPdpGallery extends HTMLElement {
  connectedCallback() {
    this._track  = this.querySelector('[data-gallery-track]');
    this._slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
    this._thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this._prev   = this.querySelector('[data-gallery-prev]');
    this._next   = this.querySelector('[data-gallery-next]');
    this._current = this.querySelector('[data-gallery-current]');
    this._idx    = 0;

    if (!this._track || this._slides.length === 0) return;

    // Thumbnail click → scroll to that slide.
    this._thumbs.forEach((btn, i) => {
      btn.addEventListener('click', () => this._goTo(i));
    });
    if (this._prev) this._prev.addEventListener('click', () => this._step(-1));
    if (this._next) this._next.addEventListener('click', () => this._step(1));

    // Active-slide detection: whichever slide is most in view wins.
    if ('IntersectionObserver' in window) {
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting && e.intersectionRatio >= 0.55) {
              const i = this._slides.indexOf(e.target);
              if (i >= 0) this._setActive(i);
            }
          });
        },
        { root: this._track, threshold: [0.55, 0.75] },
      );
      this._slides.forEach((s) => this._io.observe(s));
    } else {
      this._track.addEventListener('scroll', () => {
        this._setActive(this._nearestIdx());
      }, { passive: true });
    }

    // Keyboard arrows when the gallery is focused.
    this.setAttribute('tabindex', this.getAttribute('tabindex') || '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { this._step(-1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { this._step(1);  e.preventDefault(); }
    });

    // Desktop pointer-drag (mobile swipes natively via scroll-snap).
    this._initDrag();

    document.addEventListener('vmx:variant:changed', this._onVariantChanged.bind(this));

    // Paint the initial active state (centers slide 0, dims the queue).
    this._setActive(0);
  }

  disconnectedCallback() {
    if (this._io) this._io.disconnect();
    document.removeEventListener('vmx:variant:changed', this._onVariantChanged.bind(this));
  }

  /** Scroll the track so slide `idx` is centred (works with the edge peek). */
  _goTo(idx) {
    const slide = this._slides[idx];
    if (!slide) return;
    const left = slide.offsetLeft - (this._track.clientWidth - slide.clientWidth) / 2;
    this._track.scrollTo({ left, behavior: 'smooth' });
    this._setActive(idx);
  }

  _step(delta) {
    const len = this._slides.length;
    if (len === 0) return;
    this._goTo((this._idx + delta + len) % len);
  }

  /** Index of the slide whose centre is nearest the viewport centre. */
  _nearestIdx() {
    const c = this._track.scrollLeft + this._track.clientWidth / 2;
    let best = 0, bd = Infinity;
    this._slides.forEach((s, i) => {
      const d = Math.abs((s.offsetLeft + s.clientWidth / 2) - c);
      if (d < bd) { bd = d; best = i; }
    });
    return best;
  }

  /** Emphasise the current slide, dim the queued ones, sync counter + thumb. */
  _setActive(idx) {
    if (idx === this._idx && this._initialised) return;
    this._initialised = true;
    this._idx = idx;
    this._slides.forEach((s, i) => s.classList.toggle('is-current', i === idx));
    this._thumbs.forEach((t, i) => {
      const on = i === idx;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-pressed', String(on));
      if (on && t.scrollIntoView) {
        t.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    });
    if (this._current) this._current.textContent = String(idx + 1).padStart(2, '0');
  }

  /** Click-drag to scroll on desktop (pointer events). */
  _initDrag() {
    const track = this._track;
    let down = false, startX = 0, startLeft = 0, moved = false;
    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return; // native touch handles itself
      down = true; moved = false;
      startX = e.clientX; startLeft = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startLeft - dx;
    });
    const end = () => {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      this._goTo(this._nearestIdx());
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointerleave', end);
    // Suppress click after a drag so it doesn't trigger a tap action.
    track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
  }

  _onVariantChanged(event) {
    const { imageSrc } = event.detail || {};
    if (!imageSrc) return;
    const matchIdx = this._thumbs.findIndex((t) => t.dataset.src === imageSrc);
    if (matchIdx >= 0) this._goTo(matchIdx);
  }
}

if (!customElements.get('vmx-pdp-gallery')) {
  customElements.define('vmx-pdp-gallery', VmxPdpGallery);
}

/**
 * Ingredients table renderer.
 * Reads the JSON embedded in [data-ingredients-table] and builds a <table>
 * using DOM methods (no innerHTML). Called once on DOMContentLoaded.
 */
function renderIngredientsTable() {
  const container = document.querySelector('[data-ingredients-table]');
  if (!container) return;

  let rows;
  try {
    rows = JSON.parse(container.dataset.ingredientsTable || '[]');
  } catch {
    return;
  }
  if (!Array.isArray(rows) || rows.length === 0) return;

  const table = document.createElement('table');
  table.className = 'vmx-ingredients__table';

  // Header
  const thead = table.createTHead();
  const hRow  = thead.insertRow();
  ['Active', 'Amount', 'NRV %'].forEach((label) => {
    const th = document.createElement('th');
    th.scope = 'col';
    th.textContent = label;
    hRow.appendChild(th);
  });

  // Body
  const tbody = table.createTBody();
  rows.forEach((row, i) => {
    const tr = tbody.insertRow();
    if (i === 0) tr.classList.add('is-hero');

    const tdName = tr.insertCell();
    const strong = document.createElement('strong');
    strong.textContent = row.name || '';
    tdName.appendChild(strong);
    if (row.purpose) {
      const em = document.createElement('em');
      em.textContent = row.purpose;
      tdName.appendChild(document.createElement('br'));
      tdName.appendChild(em);
    }

    const tdAmount = tr.insertCell();
    tdAmount.textContent = row.amount || '';
    tdAmount.className   = 'vmx-ingredients__table-amount';

    const tdNrv = tr.insertCell();
    tdNrv.textContent = row.dailyValue || row.nrv || '—';
    tdNrv.className   = 'vmx-ingredients__table-nrv';
  });

  container.appendChild(table);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderIngredientsTable);
} else {
  renderIngredientsTable();
}
